import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoleLists } from '../../../shared/slices/rolesSlice';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link,
  MenuItem as Option,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { useUserInfo } from '../../../shared/UserInfo';
import usePrompt from '../../../shared/CustomPrompt';
import { useAPIProgress } from '../../../shared/APIProgress';
import { useStatusMessages } from '../../../shared/StatusMessages';
import { useToastProvider } from '../../../shared/ToastProvider';
import {
  fetchJsonWithAuth,
  fetchWithAuth
} from '../../../shared/fetch-json.util';
import {
  addPropAndValue,
  getUnitGropAndUnitsList,
  getUserName,
  isScopeAnyGlobalType,
  openLink
} from '../../../shared/UtilFunctions';
import {
  STRATEGY_APPROVER,
  STRATEGY_OWNER,
  STRATEGY_STATUS_ACTIVE,
  STRATEGY_STATUS_DRAFT,
  STRATEGY_UPDATE
} from '../../../shared/AppConstants';
import { callUserInfoAPI } from '../../../shared/APIHelper';
import { getEnvVar } from '../../../utils/getEnvVar';
import { StrategyListTableContainer } from './StrategyListTableContainer';
import OwnerDropDownSearch from '../../UserDropDownSearch';

export default function StrategyUpdates() {
  const [strategyOwnerList, setStrategyOwnerList] = useState(null);
  const [strategyApproverList, setStrategyApproverList] = useState(null);
  const [ownerInfoList, setOwnerInfoList] = useState(null);
  const [approverInfoList, setApproverInfoList] = useState(null);
  const [owners, setOwners] = useState(null);
  const info = useUserInfo();
  const tokenAvailable = info?.token;
  const isAdmin = info?.isAdmin;
  const dispatch = useDispatch();
  const {
    strategyAdmins,
    sroSBMs,
    sroCommMgrs,
    status: rolesStatus
  } = useSelector((state) => state.roles);
  const rolesProgressTracked = useRef(false);
  const [userInfoMap, setUserInfoMap] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [strategyList, setStrategyList] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [approvers, setApprovers] = useState(null);
  const [strategyUpdate, setStrategyUpdate] = useState('Owner');
  const [isUpdating, setIsUpdating] = useState(false);

  const { incrementAPIInProgress, decrementAPIInProgress } = useAPIProgress();

  const { setStatusErrorMsg, setStatusSuccessMsg, clearAllMessages } =
    useStatusMessages();

  const { enqueueToast } = useToastProvider();

  const { control, getValues, formState, setError } = useForm({
    mode: 'all'
  });
  const [newOwnerRefresh, setNewOwnerRefresh] = useState(false);

  const { isDirty, errors } = formState;

  function isFormDirty() {
    return isDirty;
  }
  usePrompt(isFormDirty());

  function callGSSAPI(searchParam) {
    let searchObj = { status: [STRATEGY_STATUS_DRAFT, STRATEGY_STATUS_ACTIVE] };

    if (searchParam && searchParam !== '') {
      addPropAndValue(
        searchObj,
        strategyUpdate === STRATEGY_OWNER ? 'ownerIds' : 'approverIds',
        searchParam.toUpperCase().split(',')
      );
      addPropAndValue(searchObj, 'embed', 'decorate');
    }
    let apiUrl = getEnvVar('VITE_GSS_API_URL') + '/searchMinimal';

    incrementAPIInProgress();

    fetchJsonWithAuth(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify(searchObj)
    })
      .then((response) => {
        let formList = response.json;
        if (!searchParam) {
          const ownerSet = new Set();
          const approverSet = new Set();

          formList.forEach((item) => {
            ownerSet.add(item.ownerId);
            approverSet.add(item.approverId);
          });

          setStrategyOwnerList([...ownerSet]);
          setStrategyApproverList([...approverSet]);
        } else {
          setStrategyList(formList);
          setNewOwnerRefresh(false);
        }
      })
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't retrieve the Strategy info. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  useEffect(() => {
    setSelectedOwner(null);
    setSelectedApprover(null);
    setNewOwnerRefresh(true);
    setStrategyList(null);
  }, [strategyUpdate]);

  useEffect(() => {
    if (rolesStatus === 'loading' && !rolesProgressTracked.current) {
      rolesProgressTracked.current = true;
      incrementAPIInProgress();
      return;
    }

    const isDone = rolesStatus === 'loaded' || rolesStatus === 'error';

    if (isDone && rolesProgressTracked.current) {
      rolesProgressTracked.current = false;
      decrementAPIInProgress();
    }

    if (rolesStatus === 'error') {
      setStatusErrorMsg(
        'Failed to load the list of owners and approvers. Please refresh the page or try again.',
        null
      );
    }
  }, [rolesStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (rolesStatus !== 'loaded') return;

    if (strategyAdmins && sroSBMs && sroCommMgrs) {
      // Load current StrategyOwners, Owners and Approver Ids -- names
      let map = new Map();

      strategyAdmins.forEach((element) => {
        map.set(element, element);
      });
      sroSBMs.forEach((element) => {
        map.set(element, element);
      });
      sroCommMgrs.forEach((element) => {
        map.set(element, element);
      });

      if (strategyOwnerList && strategyOwnerList.length > 0) {
        strategyOwnerList.forEach((element) => {
          map.set(element, element);
        });
      }

      if (strategyApproverList && strategyApproverList.length > 0) {
        strategyApproverList.forEach((element) => {
          map.set(element, element);
        });
      }

      const userIds = [...map.values()];

      callUserInfoAPI(
        userIds,
        setUserInfoMap,
        null,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        true
      );
    }
  }, [
    rolesStatus,
    strategyAdmins,
    sroSBMs,
    sroCommMgrs,
    strategyOwnerList,
    strategyApproverList
  ]);

  useEffect(() => {
    if (userInfoMap) {
      // Load Owners and Approver User Info - complete
      let ownerList = [];
      let approverList = [];

      strategyAdmins.forEach((element) => {
        ownerList.push(userInfoMap.get(element));
        approverList.push(userInfoMap.get(element));
      });
      sroSBMs.forEach((element) => {
        ownerList.push(userInfoMap.get(element));
      });
      sroCommMgrs.forEach((element) => {
        approverList.push(userInfoMap.get(element));
      });
      setOwners(ownerList);
      setApprovers(approverList);

      //load Current Strategy Owners
      let currentOwner = prepareCurrentList(strategyOwnerList);
      setOwnerInfoList(currentOwner);
      let currentApprover = prepareCurrentList(strategyApproverList);
      setApproverInfoList(currentApprover);
    }
  }, [userInfoMap]);

  function prepareCurrentList(currentUserList) {
    let owners = [];

    // Sort by Last Name, first name
    let tmp;
    let list = [];

    currentUserList?.forEach((item) => {
      let name = userInfoMap.get(item);
      name && list.push(name);
    });

    list?.sort((a, b) => {
      tmp = a.lastName.localeCompare(b.lastName);
      if (tmp === 0) {
        // Compare first name
        tmp = a.firstName.localeCompare(b.firstName);
      }
      return tmp;
    });

    list?.forEach((item) => {
      owners.push({
        value: item.id.toUpperCase(),
        label:
          item.upperLastName +
          ', ' +
          item.upperFirstName +
          ' ' +
          (item.middleInitial !== null ? ' ' + item.middleInitial : '') +
          ' - ' +
          item.id.toUpperCase()
      });
    });
    return owners;
  }

  useEffect(() => {
    callGSSAPI();
    dispatch(fetchRoleLists());
  }, [tokenAvailable, dispatch]);

  const onChange = (event) => {
    const selectedUser = event.target;

    strategyUpdate === STRATEGY_OWNER
      ? setSelectedOwner(selectedUser.value)
      : setSelectedApprover(selectedUser.value);
    callGSSAPI(selectedUser.value);
  };

  function getTableColumnHeaderInfo() {
    let cols = [
      {
        field: 'strategyId',
        headerName: 'Strategy #',
        renderCell: ({ row }) => (
          <Link
            onClick={(e) => openLink('/viewStrategy/' + row.strategyId, e)}
            sx={(theme) => ({
              color: theme.fuel6Tokens.JddcColorTextBrand,
              cursor: 'pointer',
              '&:hover': { color: theme.fuel6Tokens.JddcColorTextBrandHover }
            })}
            underline="hover"
          >
            {row.strategyId}
          </Link>
        ),
        flex: 1,
        minWidth: 110
      },
      {
        field: 'status',
        headerName: 'Status',
        valueGetter: (value, row) => row.status,
        flex: 1,
        minWidth: 100
      },
      {
        field: 'mgc',
        headerName: 'MGC',
        valueGetter: (value, row) => row.decorated?.mgcs?.sort()[0],
        renderCell: ({ row }) => displayList(row.decorated?.mgcs?.sort()),
        flex: 8,
        minWidth: 220
      },
      {
        field: 'scope',
        headerName: 'Scope',
        valueGetter: (value, row) =>
          row.decorated?.scope ? getScope(row.decorated.scope)[0] : '',
        renderCell: ({ row }) =>
          row.decorated?.scope ? (
            getScope(row.decorated.scope, true)
          ) : (
            <Typography component="span" color="text.secondary">
              Not Selected
            </Typography>
          ),
        flex: 1,
        minWidth: 140
      }
    ];
    if (strategyUpdate === STRATEGY_APPROVER)
      cols.push({
        field: 'approverId',
        headerName: 'Approver',
        valueGetter: (value, row) => getUserName(row.approverId, userInfoMap),
        flex: 2,
        minWidth: 150
      });
    else
      cols.push({
        field: 'ownerId',
        headerName: 'Owner',
        valueGetter: (value, row) => getUserName(row.ownerId, userInfoMap),
        flex: 2,
        minWidth: 150
      });

    return cols;
  }

  const columns = useMemo(() => getTableColumnHeaderInfo(), [strategyList]);

  function displayList(infoList) {
    const listItems = [];

    if (infoList?.length > 0)
      for (const element of infoList) listItems.push(<div>{element}</div>);

    return listItems;
  }

  function getScope(infoList, display) {
    let item = [];

    if (isScopeAnyGlobalType(infoList.level)) {
      item.push(infoList.level);
    } else {
      item = getUnitGropAndUnitsList(infoList);
    }

    item = item.sort();

    return display ? displayList(item) : item;
  }

  function enableUpdate() {
    let data = getValues();
    let newOwnerId = data['newUser'];

    return newOwnerId && selectedStrategy?.length > 0;
  }

  function getStrategyUpdateJsonPatch() {
    let data = getValues();
    let objectData;
    let itemLabel = [];
    let newUserId = data['newUser'];

    selectedStrategy.forEach((strategy) => itemLabel.push(strategy.strategyId));

    objectData = {};

    addPropAndValue(objectData, 'strategyIds', itemLabel);

    addPropAndValue(
      objectData,
      strategyUpdate === STRATEGY_OWNER ? 'newOwnerId' : 'newApproverId',
      newUserId.value
    );

    return JSON.stringify(objectData);
  }

  function applyStrategyUpdates() {
    let data = getValues();
    if (!data) return;

    let newOwnerId = data['newUser'];

    if (!newOwnerId) {
      setError('newUser', {
        message: 'Please select new Owner.',
        type: 'manual'
      });
      return;
    }

    if (!selectedStrategy?.length) return;

    let commonUrl = getEnvVar('VITE_GSS_API_URL') + '/bulk';
    let jsonPatch = getStrategyUpdateJsonPatch();
    let strategyCount = selectedStrategy.length;

    setIsUpdating(true);
    incrementAPIInProgress();
    enqueueToast({
      variant: 'info',
      message: `Updating ${strategyCount} strategies...`
    });
    fetchWithAuth(commonUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: jsonPatch
    })
      .then((response) => {
        if (response.status === 200) {
          clearAllMessages();
          setStatusSuccessMsg(
            'Strategy ' + strategyUpdate + ' updated successfully.'
          );
          enqueueToast({
            variant: 'success',
            message: 'Strategy ' + strategyUpdate + ' updated successfully.'
          });
          callGSSAPI(
            strategyUpdate === STRATEGY_OWNER ? selectedOwner : selectedApprover
          );
          setNewOwnerRefresh(true);
        } else if (response.status === 400) {
          setStatusErrorMsg('Bad Request!');
          enqueueToast({ variant: 'error', message: 'Bad Request!' });
        } else {
          setStatusErrorMsg(
            "Couldn't update the " + strategyUpdate + '. Please try again.'
          );
          enqueueToast({
            variant: 'error',
            message:
              "Couldn't update the " + strategyUpdate + '. Please try again.'
          });
        }
      })
      .catch((error) => {
        setStatusErrorMsg(
          "Couldn't update the " + strategyUpdate + '. Please try again.',
          error
        );
        enqueueToast({
          variant: 'error',
          message:
            "Couldn't update the " + strategyUpdate + '. Please try again.'
        });
      })
      .finally(() => {
        decrementAPIInProgress();
        setIsUpdating(false);
      });
  }

  return (
    <Box component="form">
      <Card>
        <CardContent>
          <Grid
            container
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant={'h2'}>{STRATEGY_UPDATE}</Typography>
            <ToggleButtonGroup
              aria-label="Owner"
              type="radio"
              value={strategyUpdate}
              variant={'segmented'}
            >
              <ToggleButton
                aria-label={STRATEGY_OWNER}
                value={STRATEGY_OWNER}
                id={STRATEGY_OWNER}
                onChange={(e) => setStrategyUpdate(e.target.value)}
              >
                {STRATEGY_OWNER}
              </ToggleButton>
              <ToggleButton
                aria-label={STRATEGY_APPROVER}
                value={STRATEGY_APPROVER}
                id={STRATEGY_APPROVER}
                onChange={(e) => setStrategyUpdate(e.target.value)}
              >
                {STRATEGY_APPROVER}
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid
            container
            sx={(theme) => ({
              gap: 3,
              alignItems: 'flex-end',
              my: theme.fuel6Tokens.JddcSpacingMedium
            })}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}
            >
              <Typography variant={'labelMedium'}>
                Current {strategyUpdate}
              </Typography>
              <Select
                fullWidth
                key={strategyUpdate}
                placeholder="Search by User ID or name"
                onChange={onChange}
              >
                {(strategyUpdate === STRATEGY_OWNER
                  ? ownerInfoList
                  : approverInfoList
                )?.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Box>

            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}
            >
              <Typography variant={'labelMedium'}>Update To *</Typography>
              <OwnerDropDownSearch
                key={newOwnerRefresh}
                rules={{ required: true }}
                control={control}
                name="newUser"
                masterUserList={
                  strategyUpdate === STRATEGY_OWNER ? owners : approvers
                }
              />
            </Box>

            <Button
              variant="primary"
              onClick={applyStrategyUpdates}
              disabled={!enableUpdate() || isUpdating}
              startIcon={
                isUpdating ? (
                  <CircularProgress size="1rem" color="inherit" />
                ) : undefined
              }
            >
              {isUpdating
                ? 'Updating...'
                : `Update ${selectedStrategy?.length ?? 0} Strategies`}
            </Button>
          </Grid>

          {strategyList?.length > 0 && (
            <Grid
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: theme.fuel6Tokens.JddcSpacingMedium,
                mt: theme.fuel6Tokens.JddcSpacingMedium
              })}
            >
              <Typography variant="labelMedium">Owned Strategies</Typography>
              <StrategyListTableContainer
                columns={columns}
                data={strategyList}
                isStrategyAdmin={isAdmin}
                setSelectedStrategy={setSelectedStrategy}
              />
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
